import React, { useState } from 'react';
import { MenuItem, Category } from '../types';
import { CATEGORIES } from '../constants';
import { Plus, Edit2, Trash2, Eye, EyeOff, Image, X, Tag } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLongPress } from './MobileControls';

interface MenuManagerProps {
  items: MenuItem[];
  onUpdateItem: (item: MenuItem) => void;
  onAddItem: (item: MenuItem) => void;
  onDeleteItem: (id: string) => void;
}

interface MenuItemCardProps { 
  item: MenuItem; 
  onEdit: (item: MenuItem) => void;
  onToggleActive: (item: MenuItem) => void;
  onDelete: (id: string, name: string) => void;
}

// Extracted MenuItemCard component to prevent re-renders wiping out event states
const MenuItemCard: React.FC<MenuItemCardProps> = ({ 
  item, 
  onEdit, 
  onToggleActive, 
  onDelete 
}) => {
  const longPressHandlers = useLongPress(() => onDelete(item.id, item.name));
  const isCoupon = item.price < 0;

  // Helper to stop propagation on all mouse/touch events to prevent triggering the card's long press
  const stopProp = (e: React.SyntheticEvent) => e.stopPropagation();

  return (
    <div 
       className={`bg-white p-4 rounded-2xl shadow-sm border ${item.isActive ? 'border-stone-100' : 'border-stone-200 opacity-60'} flex gap-4 select-none active:scale-95 transition-transform relative overflow-hidden`}
       {...longPressHandlers}
    >
           {isCoupon && <div className="absolute top-0 right-0 bg-red-100 text-red-600 text-[10px] font-bold px-2 py-1 rounded-bl-xl z-0">COUPON</div>}
           
           <img src={item.imageUrl} alt="" className="w-20 h-20 rounded-xl object-cover bg-stone-200 flex-shrink-0 z-0" />
           <div className="flex-1 min-w-0 z-0">
             <div className="flex justify-between items-start">
               <h3 className="font-bold text-stone-800 truncate pr-6">{item.name}</h3>
               <span className="text-xs bg-stone-100 px-2 py-1 rounded text-stone-500">{item.category}</span>
             </div>
             <p className={`text-sm mt-1 ${isCoupon ? 'text-red-500 font-bold' : 'text-stone-500'}`}>
               ¥{item.price.toLocaleString()}
             </p>
             <p className="text-xs text-stone-400 mt-2 truncate">{item.description}</p>
           </div>
           <div className="flex flex-col gap-2 justify-center ml-2 border-l border-stone-100 pl-4 relative z-10">
             <button 
               type="button"
               onMouseDown={stopProp}
               onTouchStart={stopProp}
               onClick={(e) => { stopProp(e); onEdit(item); }} 
               className="p-2 bg-stone-50 rounded-lg text-stone-600 hover:bg-stone-200 cursor-pointer"
               title="編集"
             >
               <Edit2 size={16} />
             </button>
             <button 
               type="button"
               onMouseDown={stopProp}
               onTouchStart={stopProp}
               onClick={(e) => { stopProp(e); onToggleActive(item); }} 
               className="p-2 bg-stone-50 rounded-lg text-stone-600 hover:bg-stone-200 cursor-pointer"
               title={item.isActive ? "非表示にする" : "表示する"}
             >
               {item.isActive ? <Eye size={16} /> : <EyeOff size={16} />}
             </button>
             {/* Delete button removed from card view as requested */}
           </div>
    </div>
  );
};

export const MenuManager: React.FC<MenuManagerProps> = ({ items, onUpdateItem, onAddItem, onDeleteItem }) => {
  const [editingItem, setEditingItem] = useState<Partial<MenuItem> | null>(null);

  // Get all unique categories currently in use plus defaults
  const existingCategories = Array.from(new Set([
    ...CATEGORIES.filter(c => c.id !== 'all').map(c => c.id),
    ...items.map(i => i.category)
  ]));

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem || !editingItem.name || editingItem.price === undefined) return;

    if (editingItem.id) {
      onUpdateItem(editingItem as MenuItem);
    } else {
      const newItem: MenuItem = {
        ...editingItem,
        id: Date.now().toString(),
        imageUrl: editingItem.imageUrl || 'https://via.placeholder.com/400',
        isActive: true,
        category: editingItem.category || 'other'
      } as MenuItem;
      onAddItem(newItem);
    }
    setEditingItem(null);
  };

  const handleDelete = (id: string, name: string) => {
    if (confirm(`「${name}」を削除しますか？`)) {
      onDeleteItem(id);
      setEditingItem(null);
    }
  };

  const handleToggleActive = (item: MenuItem) => {
    onUpdateItem({...item, isActive: !item.isActive});
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-cafe-text">メニュー管理</h2>
        <button
          onClick={() => setEditingItem({ category: 'drink', price: 0, isActive: true })}
          className="bg-stone-800 text-white px-4 py-2 rounded-xl flex items-center gap-2 text-sm font-bold shadow-lg hover:bg-stone-700 transition-colors"
        >
          <Plus size={16} />
          商品追加
        </button>
      </div>

      <p className="text-xs text-stone-400 text-center mb-2">長押しで削除可能</p>
      <div className="flex-1 overflow-y-auto pb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map(item => (
          <MenuItemCard 
            key={item.id} 
            item={item} 
            onEdit={setEditingItem} 
            onToggleActive={handleToggleActive}
            onDelete={handleDelete}
          />
        ))}
      </div>

      {/* Edit Modal */}
      <AnimatePresence>
        {editingItem && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl"
            >
              <div className="p-4 border-b border-stone-100 flex justify-between items-center bg-stone-50">
                <h3 className="font-bold text-lg">{editingItem.id ? '商品編集' : '新規商品'}</h3>
                <button onClick={() => setEditingItem(null)} className="p-2 hover:bg-stone-200 rounded-full transition-colors"><X size={20} /></button>
              </div>
              
              <form onSubmit={handleSave} className="p-6 space-y-4">
                <div className="space-y-1">
                   <label className="text-xs font-bold text-stone-500">商品名</label>
                   <input 
                     required
                     type="text" 
                     className="w-full bg-stone-50 border-none rounded-xl p-3 focus:ring-2 focus:ring-stone-200"
                     value={editingItem.name || ''}
                     onChange={e => setEditingItem({...editingItem, name: e.target.value})}
                   />
                </div>

                <div className="grid grid-cols-2 gap-4">
                   <div className="space-y-1">
                      <label className="text-xs font-bold text-stone-500">価格</label>
                      <input 
                        required
                        type="number" 
                        // removed min="0" to allow coupons
                        className="w-full bg-stone-50 border-none rounded-xl p-3 focus:ring-2 focus:ring-stone-200"
                        value={editingItem.price === undefined ? '' : editingItem.price}
                        onChange={e => setEditingItem({...editingItem, price: parseInt(e.target.value)})}
                        placeholder="例: 500 または -500"
                      />
                   </div>
                   
                   {/* Custom Category Input */}
                   <div className="space-y-1">
                      <label className="text-xs font-bold text-stone-500">カテゴリ</label>
                      <input 
                        required
                        type="text" 
                        className="w-full bg-stone-50 border-none rounded-xl p-3 focus:ring-2 focus:ring-stone-200"
                        value={editingItem.category || ''}
                        onChange={e => setEditingItem({...editingItem, category: e.target.value})}
                        placeholder="自由入力..."
                      />
                   </div>
                </div>
                
                {/* Category Suggestions */}
                <div className="flex flex-wrap gap-2">
                  {existingCategories.map(cat => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setEditingItem({...editingItem, category: cat})}
                      className={`text-[10px] px-2 py-1 rounded-full border ${
                        editingItem.category === cat 
                          ? 'bg-stone-800 text-white border-stone-800' 
                          : 'bg-white text-stone-500 border-stone-200 hover:border-stone-400'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>

                <div className="space-y-1">
                   <label className="text-xs font-bold text-stone-500">画像URL</label>
                   <div className="flex gap-2">
                     <input 
                       type="text" 
                       className="w-full bg-stone-50 border-none rounded-xl p-3 focus:ring-2 focus:ring-stone-200 text-xs font-mono"
                       value={editingItem.imageUrl || ''}
                       onChange={e => setEditingItem({...editingItem, imageUrl: e.target.value})}
                       placeholder="https://..."
                     />
                   </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-stone-500">説明</label>
                  <textarea 
                    className="w-full bg-stone-50 border-none rounded-xl p-3 h-20 resize-none focus:ring-2 focus:ring-stone-200"
                    value={editingItem.description || ''}
                    onChange={e => setEditingItem({...editingItem, description: e.target.value})}
                  />
                </div>

                <div className="flex gap-2 mt-4">
                  {editingItem.id && (
                     <button 
                       type="button"
                       onClick={() => handleDelete(editingItem.id!, editingItem.name!)}
                       className="px-4 py-4 rounded-xl bg-red-50 text-red-500 font-bold hover:bg-red-100 transition-colors"
                       title="削除"
                     >
                       <Trash2 size={20} />
                     </button>
                  )}
                  <button 
                    type="submit"
                    className="flex-1 bg-stone-800 text-white font-bold py-4 rounded-xl hover:bg-stone-700 transition-colors"
                  >
                    保存する
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};